<script setup>
import { computed, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import CampaignsAPI from 'dashboard/api/campaigns';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t, locale } = useI18n();
const dialogRef = ref(null);
const recipients = ref([]);
const meta = ref({ current_page: 1, per_page: 25, total_count: 0 });
const status = ref('all');
const search = ref('');
const isLoading = ref(false);
const hasError = ref(false);
const isDownloading = ref(false);

const statusOptions = computed(() => [
  { value: 'all', label: t('CAMPAIGN.WHATSAPP.RECIPIENTS.FILTERS.ALL') },
  {
    value: 'pending',
    label: t('CAMPAIGN.WHATSAPP.RECIPIENTS.FILTERS.PENDING'),
  },
  { value: 'sent', label: t('CAMPAIGN.WHATSAPP.RECIPIENTS.FILTERS.SENT') },
  {
    value: 'delivered',
    label: t('CAMPAIGN.WHATSAPP.RECIPIENTS.FILTERS.DELIVERED'),
  },
  { value: 'read', label: t('CAMPAIGN.WHATSAPP.RECIPIENTS.FILTERS.READ') },
  {
    value: 'failed',
    label: t('CAMPAIGN.WHATSAPP.RECIPIENTS.FILTERS.FAILED'),
  },
]);

const statusLabels = computed(() => ({
  pending: t('CAMPAIGN.WHATSAPP.RECIPIENTS.STATUS.PENDING'),
  sent: t('CAMPAIGN.WHATSAPP.RECIPIENTS.STATUS.SENT'),
  delivered: t('CAMPAIGN.WHATSAPP.RECIPIENTS.STATUS.DELIVERED'),
  read: t('CAMPAIGN.WHATSAPP.RECIPIENTS.STATUS.READ'),
  failed: t('CAMPAIGN.WHATSAPP.RECIPIENTS.STATUS.FAILED'),
}));

const fetchRecipients = async (page = 1) => {
  if (!props.selectedCampaign?.id) return;

  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await CampaignsAPI.getDeliveryRecipients(
      props.selectedCampaign.id,
      { page, status: status.value, search: search.value }
    );
    recipients.value = response.data.payload;
    meta.value = response.data.meta;
  } catch (error) {
    hasError.value = true;
    recipients.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = useDebounceFn(() => fetchRecipients(1), 350);

const open = () => {
  status.value = 'all';
  search.value = '';
  dialogRef.value?.open();
  fetchRecipients(1);
};

const statusLabel = value =>
  statusLabels.value[value] || statusLabels.value.pending;

const statusClass = value => ({
  'bg-n-amber-3 text-n-amber-11': value === 'pending',
  'bg-n-blue-3 text-n-blue-11': value === 'sent',
  'bg-n-teal-3 text-n-teal-11': ['delivered', 'read'].includes(value),
  'bg-n-ruby-3 text-n-ruby-11': value === 'failed',
});

const formatTimestamp = value => {
  if (!value) return '—';
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const openConversation = conversationId => {
  if (!conversationId || !props.selectedCampaign?.account_id) return;
  window.open(
    `/app/accounts/${props.selectedCampaign.account_id}/conversations/${conversationId}`,
    '_blank',
    'noopener,noreferrer'
  );
};

const downloadFailedRecipients = async () => {
  if (!props.selectedCampaign?.id || isDownloading.value) return;

  isDownloading.value = true;
  try {
    const response = await CampaignsAPI.downloadFailedRecipients(
      props.selectedCampaign.id
    );
    const campaignId =
      props.selectedCampaign.display_id || props.selectedCampaign.id;
    downloadCsvFile(`campaign-${campaignId}-failed.csv`, response.data);
  } catch (error) {
    hasError.value = true;
  } finally {
    isDownloading.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    overflow-y-auto
    :title="t('CAMPAIGN.WHATSAPP.RECIPIENTS.TITLE')"
    :description="selectedCampaign?.title"
    :show-confirm-button="false"
    :cancel-button-label="t('CAMPAIGN.WHATSAPP.RECIPIENTS.CLOSE')"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          v-model="search"
          type="search"
          class="w-full h-10 px-3 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-2 text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-blue-8"
          :placeholder="t('CAMPAIGN.WHATSAPP.RECIPIENTS.SEARCH')"
          @input="handleSearch"
        />
        <select
          v-model="status"
          class="h-10 px-3 text-sm border rounded-lg outline-none sm:w-48 border-n-weak bg-n-alpha-2 text-n-slate-12"
          @change="fetchRecipients(1)"
        >
          <option
            v-for="option in statusOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <button
          v-if="selectedCampaign?.failed_recipients_url"
          type="button"
          :disabled="isDownloading"
          class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium rounded-lg whitespace-nowrap bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4"
          @click="downloadFailedRecipients"
        >
          {{ t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.DOWNLOAD_FAILED') }}
        </button>
      </div>

      <div
        v-if="isLoading"
        class="flex items-center justify-center min-h-52 text-n-slate-11"
      >
        <Spinner />
      </div>
      <div
        v-else-if="hasError"
        class="p-6 text-sm text-center rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.ERROR') }}
      </div>
      <div
        v-else-if="!recipients.length"
        class="p-8 text-sm text-center border rounded-lg border-n-weak text-n-slate-11"
      >
        {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.EMPTY') }}
      </div>
      <div v-else class="overflow-x-auto border rounded-lg border-n-weak">
        <table class="w-full text-sm text-start">
          <thead class="bg-n-alpha-2 text-n-slate-11">
            <tr>
              <th class="px-3 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.COLUMNS.CONTACT') }}
              </th>
              <th class="px-3 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.COLUMNS.PHONE') }}
              </th>
              <th class="px-3 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.COLUMNS.STATUS') }}
              </th>
              <th class="px-3 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.COLUMNS.UPDATED') }}
              </th>
              <th class="px-3 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.COLUMNS.ERROR') }}
              </th>
              <th class="px-3 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.COLUMNS.CONVERSATION') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="recipient in recipients" :key="recipient.id">
              <td class="px-3 py-3 text-n-slate-12">
                {{ recipient.name || '—' }}
              </td>
              <td class="px-3 py-3 font-mono text-n-slate-11" dir="ltr">
                {{ recipient.phone || '—' }}
              </td>
              <td class="px-3 py-3">
                <span
                  class="inline-flex px-2 py-1 text-xs font-medium rounded-md"
                  :class="statusClass(recipient.status)"
                >
                  {{ statusLabel(recipient.status) }}
                </span>
              </td>
              <td class="px-3 py-3 whitespace-nowrap text-n-slate-11">
                {{ formatTimestamp(recipient.last_status_at) }}
              </td>
              <td class="max-w-52 px-3 py-3 text-n-ruby-11">
                <span class="line-clamp-2" :title="recipient.error">
                  {{ recipient.error || recipient.failure_code || '—' }}
                </span>
              </td>
              <td class="px-3 py-3">
                <Button
                  v-if="recipient.conversation_id"
                  variant="faded"
                  color="slate"
                  size="sm"
                  icon="i-lucide-message-square"
                  :title="t('CAMPAIGN.WHATSAPP.RECIPIENTS.OPEN_CONVERSATION')"
                  @click="openConversation(recipient.conversation_id)"
                />
                <span v-else>
                  {{ t('CAMPAIGN.WHATSAPP.RECIPIENTS.NOT_AVAILABLE') }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <PaginationFooter
        v-if="meta.total_count > meta.per_page"
        :current-page="Number(meta.current_page)"
        :total-items="Number(meta.total_count)"
        :items-per-page="Number(meta.per_page)"
        class="!px-0"
        @update:current-page="fetchRecipients"
      />
    </div>
  </Dialog>
</template>
